import { toaster } from "@/components/ui/toaster";
import { AppRoutes } from "@/constants/routes";
import { Sucursal } from "@/services/accounts.service";
import {
  createBOUser,
  CreateBOUserProps,
  deleteUsersBo,
  getActiveDirectoryUsers,
  getBOUsers,
  updateBOUser,
  UpdateBOUserProps,
} from "@/services/users-bo.service";
import { handlePermissionError } from "@/utils/permission-check";
import { AxiosError } from "axios";
import { Dispatch, SetStateAction } from "react";
import {
  FieldValues,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import { NavigateFunction } from "react-router-dom";
import { getRAFilterRequest, RAFProps } from "./filter-obj";
import { Option } from "@/components/select/AsyncAutocompleteSelect";

interface ClearFiltersHandlerProps {
  setValue: UseFormSetValue<FieldValues>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  submitFilters?: (event: FieldValues) => Promise<void>;
  getValues: UseFormGetValues<FieldValues>;
}

export const clearFiltersHandler = async ({
  setValue,
  setFiltersApplied,
  submitFilters,
  getValues,
}: ClearFiltersHandlerProps) => {
  setValue("full_name", "");
  setValue("mail", "");
  setValue("assignated_roles", []);
  setFiltersApplied(false);

  submitFilters?.(getValues());
};

interface SubmitFiltersHandlerProps {
  event: FieldValues;
  fetchBOUsers: (filters: any, flag?: boolean) => Promise<void>;
  fetchPermissions: () => Promise<void>;
  navigate: NavigateFunction;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setCurrentFilters: Dispatch<SetStateAction<any>>;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  setLoadingBOUsers: Dispatch<SetStateAction<boolean>>;
}

export const submitFiltersHandler = async ({
  event,
  fetchBOUsers,
  fetchPermissions,
  navigate,
  setFiltersApplied,
  setCurrentFilters,
  setOpenFilters,
  setLoadingBOUsers,
}: SubmitFiltersHandlerProps) => {
  try {
    setLoadingBOUsers(true);
    setFiltersApplied(true);
    const filterObj = getRAFilterRequest(event as RAFProps);

    setCurrentFilters(filterObj);
    if (!Object.keys(filterObj)?.length) setFiltersApplied(false);
    await fetchBOUsers(filterObj);

    setOpenFilters(false);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.response?.data?.message ??
          "No se pudieron aplicar los filtros.",
      });
    }
    throw error;
  } finally {
    setLoadingBOUsers(false);
  }
};

interface HandleSucursalSelectionProps {
  setValue: UseFormSetValue<FieldValues>;
  selectedIds: string[];
  isGeneralSelected: boolean;
  setIsGeneralSelected: Dispatch<SetStateAction<boolean>>;
  sucursales?: Sucursal[] | null;
}

export const sucursalSelectionHandler = ({
  sucursales,
  isGeneralSelected,
  selectedIds,
  setIsGeneralSelected,
  setValue,
}: HandleSucursalSelectionProps) => {
  const GENERAL_ID = "GEN"

  if (selectedIds.includes(GENERAL_ID)) {
    if (isGeneralSelected) {
      // GENERAL ya estaba seleccionado
      setValue("sucursales", []);
      setIsGeneralSelected(false);
    } else {
      // GENERAL es nueva selección
      setValue("sucursales", sucursales?.map((su) => su.clave) || []);
      setIsGeneralSelected(true);
    }
  } else if (isGeneralSelected) {
    // GENERAL se está deseleccionando
    setValue("sucursales", []);
    setIsGeneralSelected(false);
  } else {
    //  Usar las selecciones individuales
    setValue("sucursales", selectedIds);
  }
};

interface DeleteAssignationParams {
  BOUsers: { listado: { usuario: string }[] };
  selection: string[];
  currentFilters: any;
  fetchPermissions: () => Promise<void>;
  fetchBOUsers: (filters: any, flag: boolean) => Promise<void>;
  setOpenConfirmModal: (val: boolean) => void;
  setLoadingDelete: (val: boolean) => void;
  navigate: NavigateFunction;
}

export const handleDeleteAssignationExternal = async ({
  BOUsers,
  selection,
  currentFilters,
  fetchPermissions,
  fetchBOUsers,
  setOpenConfirmModal,
  setLoadingDelete,
  navigate,
}: DeleteAssignationParams) => {
  try {
    setLoadingDelete(true);

    const usersToDelete = BOUsers?.listado.filter((user) =>
      selection?.includes(user.usuario),
    );

    await deleteUsersBo({
      usuarios: usersToDelete.map((el) => el.usuario),
    });

    await fetchPermissions();
    await fetchBOUsers(currentFilters, false);

    toaster.create({
      type: "success",
      title: "Usuarios eliminados con éxito.",
    });

    setOpenConfirmModal(false);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.USER_ROLES,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.response?.data?.message ??
          "No se ha podido eliminar a los usuarios.",
      });
    }
    throw error;
  } finally {
    setLoadingDelete(false);
    setOpenConfirmModal(false);
  }
};

interface HandleCreateRoleAssignationParams {
  data: FieldValues;
  fetchPermissions: () => Promise<void>;
  navigate: NavigateFunction;
  setLoadingCreateUserBO: (val: boolean) => void;
  redirectPath: string;
}

export const handleCreateRoleAssignation = async ({
  data,
  fetchPermissions,
  navigate,
  setLoadingCreateUserBO,
  redirectPath,
}: HandleCreateRoleAssignationParams) => {
  try {
    setLoadingCreateUserBO(true);

    const payload: CreateBOUserProps = {
      usuario: data.user?.value,
      rolesAAsignar: data.roles,
      nombreApellido: data.full_name,
      sucursales: data.sucursales,
      mail: data.mail,
    };
    await createBOUser(payload);
    await fetchPermissions();

    toaster.create({
      type: "success",
      title: "Roles y sucursales asignados con éxito.",
    });

    navigate(redirectPath);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.USER_ROLES,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.response?.data?.message ??
          "No se pudo asignar los roles y sucursales al usuario.",
      });
    }
    throw error;
  } finally {
    setLoadingCreateUserBO(false);
  }
};

interface HandleUpdateRoleAssignationParams {
  data: FieldValues;
  fetchPermissions: () => Promise<void>;
  navigate: NavigateFunction;
  setLoadingUser: (val: boolean) => void;
  redirectPath: string;
}

export const handleUpdateRoleAssignation = async ({
  data,
  fetchPermissions,
  navigate,
  setLoadingUser,
  redirectPath,
}: HandleUpdateRoleAssignationParams) => {
  try {
    setLoadingUser(true);

    const payload: UpdateBOUserProps = {
      usuario: data.user?.[0],
      rolesAAsignar: data.roles,
      sucursales: data?.sucursales,
    };


    await updateBOUser(payload);
    await fetchPermissions();

    toaster.create({
      type: "success",
      title: "Roles y sucursales actualizados con éxito",
    });

    navigate(redirectPath);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.USER_ROLES,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.response?.data?.message ??
          "No se pudo actualizar los roles y sucursales del usuario.",
      });
    }
    throw error;
  } finally {
    setLoadingUser(false);
  }
};

interface FetchBOUsersHandlerProps {
  filters?: any;
  showLoad?: boolean;
  setBOUsers: Dispatch<SetStateAction<any>>;
  setLoadingBOUsers: Dispatch<SetStateAction<boolean>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  setCurrentFilters: Dispatch<SetStateAction<any>>;
}

export const fetchBOUsersHandler = async ({
  filters,
  showLoad,
  setBOUsers,
  setLoadingBOUsers,
  navigate,
  fetchPermissions,
  setCurrentFilters,
}: FetchBOUsersHandlerProps) => {
  try {
    if (showLoad) setLoadingBOUsers(true);

    const response = await getBOUsers({
      ...filters,
      "P.Pagina": filters?.["P.Pagina"] ?? 1,
      "P.Records": 10,
    });

    setCurrentFilters({
      ...filters,
      "P.Pagina": filters?.["P.Pagina"] ?? 1,
      "P.Records": 10,
    });

    setBOUsers(response);

    setLoadingBOUsers(false);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: "No se pudo cargar el listado de usuarios.",
      });
    }
  } finally {
    setLoadingBOUsers(false);
  }
};



export interface GetAccountOptionsHandlerProps {
  inputValue: string;
}

export const getOptionsHandler = async ({
  inputValue,
}: GetAccountOptionsHandlerProps) => {
  const filtered = await getActiveDirectoryUsers({
    busqueda: inputValue,
  });

  return (
    filtered?.map((p) => ({
      label: `${p.displayName} - ${p.mail}`,
      value: p.id
    })) ?? []
  );
};

export interface LoadOptionsHandlerProps {
  timeoutRef: { current: ReturnType<typeof setTimeout> | null };
  inputValue: string;
  getOptions: (inputValue: string) => Promise<Option[]>;
  setLoading: Dispatch<SetStateAction<boolean>>;
}


export const loadOptionsHandler = ({
  timeoutRef,
  inputValue,
  getOptions,
  setLoading,
}: LoadOptionsHandlerProps): Promise<Option[]> => {
  return new Promise((resolve) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef?.current);
    }

    timeoutRef.current = setTimeout(async () => {
      try {
        if (inputValue.trim().length < 3) {
          setLoading(false);
          resolve([]);
          return;
        }

        setLoading(true);
        const options = await getOptions(inputValue);
        resolve(options);
      } catch (e) {
        resolve([]);
        throw e;
      } finally {
        setLoading(false);
      }
    }, 1000);
  });
};